import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createEvent, listEvents } from '../api/client';
import type { CreateEventBody } from '../api/client';

export function useUpcomingEvents(locationId: string | null, days = 7) {
  return useQuery({
    queryKey: ['events', 'upcoming', locationId, days],
    queryFn: () => {
      const from = new Date();
      from.setHours(0, 0, 0, 0);
      const to = new Date(from);
      to.setDate(to.getDate() + days - 1);
      to.setHours(23, 59, 59, 999);
      return listEvents({
        from: from.toISOString(),
        to:   to.toISOString(),
        ...(locationId ? { locationId: [locationId] } : {}),
        limit: 200,
      });
    },
    staleTime: 5 * 60_000,
  });
}

export function useEvents48h(locationId: string | null) {
  return useQuery({
    queryKey: ['events', '48h', locationId],
    queryFn: () => {
      const now     = new Date();
      const plus48h = new Date(now.getTime() + 48 * 60 * 60 * 1000);
      return listEvents({
        from:  now.toISOString(),
        to:    plus48h.toISOString(),
        ...(locationId ? { locationId: [locationId] } : {}),
        limit: 50,
      });
    },
    staleTime: 2 * 60_000,
  });
}

export function useEventsForMonth(
  locationId: string | null,
  year: number,
  month: number,
) {
  return useQuery({
    queryKey: ['events', 'month', locationId, year, month],
    queryFn: () => {
      const from = new Date(year, month, 1);
      const to   = new Date(year, month + 1, 1);
      return listEvents({
        from:  from.toISOString(),
        to:    to.toISOString(),
        ...(locationId ? { locationId: [locationId] } : {}),
        limit: 200,
      });
    },
    staleTime: 5 * 60_000,
  });
}

export function buildThreeDayWindow(now: Date): { from: string; to: string } {
  const from = new Date(now);
  from.setHours(0, 0, 0, 0);
  const to = new Date(now);
  to.setDate(to.getDate() + 2);
  to.setHours(23, 59, 59, 999);
  return { from: from.toISOString(), to: to.toISOString() };
}

export function useEvents3Day(locationId: string | null) {
  return useQuery({
    queryKey: ['events', '3day', locationId],
    queryFn: () => {
      const { from, to } = buildThreeDayWindow(new Date());
      return listEvents({
        from,
        to,
        limit: 100,
        ...(locationId ? { locationId: [locationId] } : {}),
      });
    },
    staleTime: 2 * 60_000,
  });
}

export function useCreateEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateEventBody) => createEvent(body),
    onSuccess:  () => { void qc.invalidateQueries({ queryKey: ['events'] }); },
  });
}
